import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Eye, EyeOff, Loader2, FolderOpen } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ name: '', slug: '', is_visible: true });
  const [saving, setSaving] = useState(false);

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/categories`, { headers: getAuthHeader() });
      setCategories(res.data);
    } catch (err) {
      toast({ title: "Error", description: "Failed to fetch categories", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  const handleNameChange = (value) => {
    setFormData({
      ...formData,
      name: value,
      slug: editing ? formData.slug : generateSlug(value)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/admin/categories/${editing}`, formData, { headers: getAuthHeader() });
        toast({ title: "Updated", description: "Category updated successfully" });
      } else {
        await axios.post(`${API}/admin/categories`, formData, { headers: getAuthHeader() });
        toast({ title: "Created", description: "Category created successfully" });
      }
      setShowForm(false);
      setEditing(null);
      setFormData({ name: '', slug: '', is_visible: true });
      fetchCategories();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to save category';
      toast({ title: "Error", description: msg, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleVisibility = async (cat) => {
    try {
      await axios.put(`${API}/admin/categories/${cat.id}`, {
        is_visible: !cat.is_visible
      }, { headers: getAuthHeader() });
      toast({
        title: cat.is_visible ? "Category Hidden" : "Category Visible",
        description: `"${cat.name}" is now ${cat.is_visible ? 'hidden from' : 'visible on'} the public storefront.`
      });
      fetchCategories();
    } catch (err) {
      toast({ title: "Error", description: "Failed to update visibility", variant: "destructive" });
    }
  };

  const handleEdit = (cat) => {
    setEditing(cat.id);
    setFormData({ name: cat.name, slug: cat.slug, is_visible: cat.is_visible });
    setShowForm(true);
  };

  const handleDelete = async (cat) => {
    if (!window.confirm(`Delete "${cat.name}" category? Products in this category will NOT be deleted but will become uncategorized.`)) return;
    try {
      await axios.delete(`${API}/admin/categories/${cat.id}`, { headers: getAuthHeader() });
      toast({ title: "Deleted", description: `"${cat.name}" category deleted` });
      fetchCategories();
    } catch (err) {
      toast({ title: "Error", description: "Failed to delete category", variant: "destructive" });
    }
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditing(null);
    setFormData({ name: '', slug: '', is_visible: true });
  };

  return (
    <div className="p-4 sm:p-8" data-testid="admin-categories-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold text-slate-800">Categories</h1>
          <p className="text-slate-600 mt-1 text-sm">Manage product categories. Hidden categories and their products won't appear on the storefront.</p>
        </div>
        <Button
          className="bg-purple-600 hover:bg-purple-700 text-white"
          onClick={() => { setShowForm(true); setEditing(null); setFormData({ name: '', slug: '', is_visible: true }); }}
          data-testid="add-category-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Category
        </Button>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <Card className="border-2 border-purple-200 mb-6" data-testid="category-form">
          <CardHeader>
            <CardTitle className="text-lg">{editing ? 'Edit Category' : 'New Category'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="cat-name">Category Name</Label>
                  <Input
                    id="cat-name"
                    value={formData.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    placeholder="e.g. Office Supplies"
                    required
                    data-testid="category-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="cat-slug">Slug (URL identifier)</Label>
                  <Input
                    id="cat-slug"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    placeholder="e.g. office-supplies"
                    required
                    data-testid="category-slug-input"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="cat-visible"
                  checked={formData.is_visible}
                  onChange={(e) => setFormData({ ...formData, is_visible: e.target.checked })}
                  className="h-4 w-4 rounded border-slate-300"
                  data-testid="category-visible-checkbox"
                />
                <Label htmlFor="cat-visible" className="text-sm">Visible on public storefront</Label>
              </div>
              <div className="flex gap-3">
                <Button type="submit" className="bg-purple-600 hover:bg-purple-700 text-white" disabled={saving} data-testid="save-category-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {editing ? 'Update Category' : 'Create Category'}
                </Button>
                <Button type="button" variant="outline" onClick={cancelForm}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Categories List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
        </div>
      ) : categories.length === 0 ? (
        <Card className="border-2 border-dashed">
          <CardContent className="p-8 text-center">
            <FolderOpen className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No categories yet. Add your first category to get started.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="categories-list">
          {categories.map((cat) => (
            <Card key={cat.id} className={`border-2 ${!cat.is_visible ? 'border-dashed border-slate-300 bg-slate-50' : ''}`} data-testid={`category-item-${cat.slug}`}>
              <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${cat.is_visible ? 'bg-green-500' : 'bg-slate-300'}`} />
                  <div>
                    <h3 className={`font-semibold ${cat.is_visible ? 'text-slate-800' : 'text-slate-400'}`}>
                      {cat.name}
                    </h3>
                    <p className="text-xs text-slate-500">slug: {cat.slug}</p>
                  </div>
                  {!cat.is_visible && (
                    <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full font-medium">
                      Hidden
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleToggleVisibility(cat)}
                    title={cat.is_visible ? 'Hide from public' : 'Make visible'}
                    data-testid={`toggle-visibility-${cat.slug}`}
                  >
                    {cat.is_visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleEdit(cat)} data-testid={`edit-category-${cat.slug}`}>
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(cat)} data-testid={`delete-category-${cat.slug}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminCategories;
